import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElementLayoutGroupTemplate,
} from '@anchorage/graphql/generated';
import { KYCQuestionRuleType } from '@anchorage/graphql/generated';

type GroupHasSomeAnswerProps = {
  groupQuestions: KYCQuestionDefinition[];
  groupName: string;
  groupIndex: number;
  getValues: (name: string) => { freeformText?: string };
};
export const groupHasSomeAnswer = ({
  groupQuestions,
  groupName,
  groupIndex,
  getValues,
}: GroupHasSomeAnswerProps) =>
  groupQuestions
    .filter(({ hidden }) => !hidden)
    .some(({ id }) => {
      const questionName = `${groupName}.${groupIndex}.${id}`;
      return !!getValues(questionName)?.freeformText; // TODO: check for other types
    });

type ShowEditButtons = {
  triggerOptionIdForModal: string;
  groupQuestions: KYCQuestionDefinition[];
  groupName: string;
  groupIndex: number;
  getValues: (name: string) => { options?: string[] | string };
};
export const shouldShowEditButton = ({
  triggerOptionIdForModal,
  groupQuestions,
  groupName,
  groupIndex,
  getValues,
}: ShowEditButtons) => {
  return groupQuestions.some(({ id }) => {
    const questionName = `${groupName}.${groupIndex}.${id}`;
    const questionValue = getValues(questionName)?.options;

    return typeof questionValue === 'string'
      ? triggerOptionIdForModal === questionValue
      : triggerOptionIdForModal === questionValue?.[0];
  });
};

type getGroupIconNameProps = {
  iconTemplate: string;
  groupName: string;
  groupIndex: number;
  getValues: (name: string) => { freeformText?: string };
};

export const getGroupIconFromTemplate = ({
  iconTemplate,
  groupName,
  groupIndex,
  getValues,
}: getGroupIconNameProps) => {
  const groupIconTemplateQuestions = iconTemplate.split('$') || [];

  const groupIconTemplate = groupIconTemplateQuestions
    ?.reduce(
      (acc, id) => {
        const questionValue = getValues(
          `${groupName}.${groupIndex}.${id.trim()}`,
        )?.freeformText;
        if (!questionValue) {
          return acc;
        }
        return [...acc, questionValue];
      },
      [''],
    )
    .filter((item) => item)
    .join(' ');

  return groupIconTemplate;
};

type getGroupItemNameProps = {
  groupTemplate?: KYCQuestionnaireElementLayoutGroupTemplate | null;
  groupName: string;
  groupIndex: number;
  getValues: (name: string) => { freeformText?: string };
};

export const getGroupItemNameFromTemplate = ({
  groupTemplate,
  groupName,
  groupIndex,
  getValues,
}: getGroupItemNameProps) => {
  const emptyGroupItemTemplate = groupTemplate?.newItemTemplate?.replace(
    /\$i/g,
    `${groupIndex + 1}`,
  );

  const groupItemTemplateQuestions =
    groupTemplate?.itemTemplate?.split('$') || [];

  const groupItemTemplate = groupItemTemplateQuestions
    ?.reduce(
      (acc, id) => {
        const questionValue = getValues(
          `${groupName}.${groupIndex}.${id.trim()}`,
        )?.freeformText;
        if (!questionValue) {
          return acc;
        }
        return [...acc, questionValue];
      },
      [''],
    )
    .filter((item) => item)
    .join(' ');

  const groupSubItemTemplateQuestions =
    groupTemplate?.itemDescriptionTemplate?.split('$') || [];

  const groupSubItemTemplate = groupSubItemTemplateQuestions
    ?.reduce(
      (acc, id) => {
        const questionValue = getValues(
          `${groupName}.${groupIndex}.${id.trim()}`,
        )?.freeformText;
        if (!questionValue) {
          return acc;
        }
        return [...acc, questionValue];
      },
      [''],
    )
    .filter((item) => item)
    .join(' ');

  return groupItemTemplate
    ? {
        groupItemName: groupItemTemplate,
        groupItemDescription: groupSubItemTemplate,
      }
    : { groupItemName: emptyGroupItemTemplate, groupItemDescription: '' };
};

/**
 * Extracts min and max size from question rules.
 * This is needed for both Yup and non-Yup modes to control UI behavior
 * (e.g., auto-populate minimum items, hide remove button at minimum).
 *
 * @param rules - Array of validation rules from the question
 * @returns Object with min and max values
 */
export const getMinMaxFromRules = (
  rules: KYCQuestionDefinition['rules'],
): { min: number; max: number | undefined } => {
  let min = 1;
  let max: number | undefined = undefined;

  if (rules) {
    const minLengthRule = rules.find(
      (rule) => rule.type === KYCQuestionRuleType.MIN_LENGTH,
    );
    const maxLengthRule = rules.find(
      (rule) => rule.type === KYCQuestionRuleType.MAX_LENGTH,
    );

    if (minLengthRule) {
      min = Number.parseInt(minLengthRule.value, 10);
    }

    if (maxLengthRule) {
      max = Number.parseInt(maxLengthRule.value, 10);
    }
  }

  return { min, max };
};
